export const UNSUPPORTED_FILTER_MESSAGE = '新普惠平台暂不支持产品筛选'

/**
 * 新普惠平台固定枚举。cd 是概而论内部字典编码，phjrCd 才是平台业务值。
 */
export const PRODUCT_ENUM_GROUPS = {
  reqSrc: [
    { cd: '360175.05', phjrCd: 'bank', label: '银行' },
    { cd: '360175.10', phjrCd: 'gel', label: '概而论' }
  ],
  customerTypes: [
    { cd: '360180.05', phjrCd: '0', label: '个人' },
    { cd: '360180.10', phjrCd: '1', label: '企业' },
    { cd: '360180.15', phjrCd: '2', label: '个体工商户' }
  ],
  acceptModes: [
    { cd: '360185.05', phjrCd: '0', label: '线上跳转' },
    { cd: '360185.10', phjrCd: '1', label: '线下办理' },
    { cd: '360185.15', phjrCd: '2', label: '纯线上办理' },
    { cd: '360185.20', phjrCd: '3', label: '小程序' }
  ],
  payModes: [
    { cd: '360190.05', phjrCd: '0', label: '等额本息' },
    { cd: '360190.10', phjrCd: '1', label: '等额本金' },
    { cd: '360190.15', phjrCd: '2', label: '随借随还' },
    { cd: '360190.20', phjrCd: '3', label: '一次性还本付息' },
    { cd: '360190.25', phjrCd: '4', label: '按月付息到期还本' },
    { cd: '360190.30', phjrCd: '5', label: '按月付息任意还本' },
    { cd: '360190.35', phjrCd: '6', label: '分期还款' }
  ],
  usageInfs: [
    { cd: '360195.05', phjrCd: '0', label: '消费' },
    { cd: '360195.10', phjrCd: '1', label: '经营' }
  ],
  productTypes: [
    { cd: '360200.05', phjrCd: '0', label: '央行政策工具支持贷款' },
    { cd: '360200.10', phjrCd: '1', label: '创业创新支持贷款' },
    { cd: '360200.15', phjrCd: '2', label: '特色产品' },
    { cd: '360200.20', phjrCd: '3', label: '抗疫（复工复产）类贷款' },
    { cd: '360200.25', phjrCd: '4', label: '制造业贷款' },
    { cd: '360200.30', phjrCd: '5', label: '外贸类贷款' },
    { cd: '360200.35', phjrCd: '6', label: '支农支小贷款' },
    { cd: '360200.40', phjrCd: '7', label: '无形资产质押贷款' },
    { cd: '360200.45', phjrCd: '8', label: '税务贷' },
    { cd: '360200.50', phjrCd: '9', label: '其他' },
    { cd: '360200.55', phjrCd: '10', label: '文旅特色产品' },
    { cd: '360200.60', phjrCd: '11', label: '知识产权质押' }
  ],
  productSorts: [
    { cd: '360205.05', phjrCd: 'maxLoanTerms', label: '期限排序' },
    { cd: '360205.10', phjrCd: 'minInterestRates', label: '利率排序' },
    { cd: '360205.15', phjrCd: 'maxCreditAmount', label: '金额排序' }
  ]
}

const PRODUCT_FILTER_FIELDS = ['acceptMode', 'userType', 'payMode', 'productType', 'usageInf']
const PRODUCT_FILTER_VALUES = {
  acceptMode: ['0', '1'],
  userType: ['0', '1'],
  payMode: ['0', '1', '2', '3', '4', '5', '6'],
  productType: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'],
  usageInf: ['0', '1']
}
const PRODUCT_SORT_PARAMS = ['maxLoanTerms', 'minInterestRates', 'maxCreditAmount']
const hasDisplayValue = value => value !== null && value !== undefined && value !== ''
const displayValue = value => hasDisplayValue(value) ? value : '--'
const firstValue = value => Array.isArray(value) ? value[0] : value
const arrayDisplayValue = value => Array.isArray(value)
  ? displayValue(value.filter(hasDisplayValue).join('、'))
  : displayValue(value)

const amountText = product => {
  if (!hasDisplayValue(product?.minCreditAmount) || !hasDisplayValue(product?.maxCreditAmount)) {
    return '--'
  }
  return `${product.minCreditAmount}万元 - ${product.maxCreditAmount}万元`
}

const termText = product => {
  if (!hasDisplayValue(product?.maxLoanTerms) || !hasDisplayValue(product?.loanTermsUnitDesc)) {
    return '--'
  }
  return `${product.maxLoanTerms}${product.loanTermsUnit === '12' ? '' : '个'}${product.loanTermsUnitDesc}`
}

const rateText = product => {
  if (!hasDisplayValue(product?.minInterestRates) || !hasDisplayValue(product?.maxInterestRates)) {
    return '--'
  }
  return `${product.minInterestRates}%-${product.maxInterestRates}%`
}

/**
 * 构造已上架产品分页请求。平台页码从 1 开始，固定透传新版五字段白名单。
 * 筛选条件仅接受数组，过滤非法值和重复值后最多保留 10 项；排序最多保留 3 项。
 *
 * @param {number|string} page 当前页码
 * @param {number|string} rows 每页数量
 * @param {Object<string, Array<string|number>>} filters 产品多选筛选
 * @param {Array<{sortParam: string, direction: string}>} sortDatas 多级排序条件
 * @returns {{page: string, rows: string, filterData: Object<string, Array<string>>, sortDatas: Array<{sortParam: string, direction: string}>}}
 */
export const buildProductListRequest = (page, rows, filters = {}, sortDatas = []) => {
  const filterData = PRODUCT_FILTER_FIELDS.reduce((result, field) => {
    const allowedValues = PRODUCT_FILTER_VALUES[field]
    result[field] = Array.isArray(filters[field])
      ? [...new Set(filters[field]
        .filter(hasDisplayValue)
        .map(String)
        .filter(value => allowedValues.includes(value)))]
        .slice(0, 10)
      : []
    return result
  }, {})

  const seenSortParams = new Set()
  const normalizedSortDatas = Array.isArray(sortDatas)
    ? sortDatas.reduce((result, sortData) => {
      const sortParam = sortData?.sortParam
      const direction = typeof sortData?.direction === 'string'
        ? sortData.direction.toUpperCase()
        : ''
      if (result.length < 3 && PRODUCT_SORT_PARAMS.includes(sortParam) &&
          ['ASC', 'DESC'].includes(direction) && !seenSortParams.has(sortParam)) {
        seenSortParams.add(sortParam)
        result.push({ sortParam, direction })
      }
      return result
    }, [])
    : []

  return {
    page: String(page),
    rows: String(rows),
    filterData,
    sortDatas: normalizedSortDatas
  }
}

/**
 * 将产品列表项转换为卡片展示模型。
 *
 * 原始字段通过展开运算完整保留，其中 productType、productTypeDesc 始终保持数组，
 * 仅对明确只展示首项的机构名和担保方式生成额外展示字段。
 *
 * @param {Object} product 产品列表项
 * @returns {Object} 产品卡片展示模型
 */
export const normalizeProductCard = (product = {}) => ({
  ...product,
  orgName: displayValue(firstValue(product.productOrgName)),
  amountText: amountText(product),
  termText: termText(product),
  rateText: rateText(product),
  guaranteeText: displayValue(firstValue(product.guaranteeModeDesc))
})

/**
 * 将 k3002 产品详情转换为页面展示模型。
 *
 * 不读取已从详情顶层移除的 enabled；productOrg 仅用于读取机构名称，
 * 不改变 productOrg.enabled 等平台原始字段的业务含义。
 *
 * @param {Object} product k3002 产品详情
 * @returns {Object} 产品详情展示模型
 */
export const normalizeProductDetail = (product = {}) => ({
  id: product.productId,
  bankName: arrayDisplayValue(product.productOrgName || product.productOrg?.orgName),
  title: displayValue(product.productName || product.productTitle),
  description: arrayDisplayValue(product.productTypeDesc),
  ratesUnitDesc: displayValue(product.ratesUnitDesc),
  amountText: amountText(product),
  termText: termText(product),
  rateText: rateText(product),
  guaranteeText: displayValue(firstValue(product.guaranteeModeDesc)),
  basicInfo: {
    target: displayValue(product.customerTypeDesc),
    purpose: displayValue(Array.isArray(product.usageInfDesc) ? product.usageInfDesc.join('、') : product.usageInfDesc),
    mode: arrayDisplayValue(product.acceptModeDesc),
    repayment: displayValue(Array.isArray(product.payModeDesc) ? product.payModeDesc.join('、') : product.payModeDesc)
  }
})

/**
 * 解析产品详情的扩展描述。
 *
 * 后端直接返回结构化对象数组；结构不合法的条目会被忽略，保证详情页继续展示基础信息。
 *
 * @param {Array<{descType: string, content: Array}>} productDescs 产品描述序列
 * @returns {{loanprocess: Array, specialities: Array, materials: Array, terms: Array}}
 */
export const parseProductDescriptions = productDescs => {
  const createEmpty = () => ({
    loanprocess: [], specialities: [], materials: [], terms: []
  })
  if (!Array.isArray(productDescs)) return createEmpty()

  return productDescs.reduce((groups, item) => {
    if (groups[item?.descType] && Array.isArray(item.content)) {
      groups[item.descType].push(...item.content)
    }
    return groups
  }, createEmpty())
}

/**
 * 按相同下标配对贷款用途业务值与名称。
 *
 * @param {Object} product 产品详情
 * @returns {Array<{value: string, label: string}>} 申请弹窗下拉选项
 */
export const getUsageOptions = (product = {}) => {
  const values = Array.isArray(product.usageInf) ? product.usageInf : []
  const labels = Array.isArray(product.usageInfDesc) ? product.usageInfDesc : []
  return values
    .filter(hasDisplayValue)
    .map((value, index) => ({
      value: String(value),
      label: hasDisplayValue(labels[index]) ? labels[index] : String(value)
    }))
}

/**
 * 校验用户输入的贷款金额。
 *
 * 申请金额以元填写和提交，产品额度以万元返回，因此比较前需转换为元；
 * 平台只接受精确到百元的申请金额。
 *
 * @param {string|number} value 用户输入金额（元）
 * @param {string|number} minWan 产品最小额度（万元）
 * @param {string|number} maxWan 产品最大额度（万元）
 * @returns {string|null} 校验错误信息，通过时返回 null
 */
export const validateLoanAmount = (value, minWan, maxWan) => {
  if (value === '' || value === null || value === undefined || !/^\d+(\.\d+)?$/.test(String(value))) {
    return '贷款金额格式不正确'
  }
  const amount = Number(value)
  if (amount <= 0) return '贷款金额必须大于0'
  if (!Number.isInteger(amount / 100)) return '贷款金额最多精确到百元'

  const minYuan = hasDisplayValue(minWan) ? Number(minWan) * 10000 : null
  const maxYuan = hasDisplayValue(maxWan) ? Number(maxWan) * 10000 : null
  if ((Number.isFinite(minYuan) && amount < minYuan) ||
      (Number.isFinite(maxYuan) && amount > maxYuan)) {
    return '贷款金额必须符合额度范围！'
  }
  return null
}

/**
 * 合并机构分页选项，并按机构编码 orgNo 去重。
 *
 * @param {Array<Object>} current 已加载机构
 * @param {Array<Object>} incoming 新一页机构
 * @returns {Array<Object>} 合并后的机构列表
 */
export const mergeOrgOptions = (current = [], incoming = []) => {
  const seen = new Set()
  return [...current, ...incoming].filter(item => {
    if (!item?.orgNo || seen.has(item.orgNo)) return false
    seen.add(item.orgNo)
    return true
  })
}

/**
 * 按产品贷款对象组装提交报文。
 *
 * customerType 直接使用产品返回值；企业和个人字段互斥。期限统一按月提交，
 * 意向机构仅使用 orgNos，保留最多 3 个的校验由申请弹窗负责。
 *
 * @param {{values: Object, product: Object, phoneAuthCode: string}} params 申请上下文
 * @returns {Object} submitLoanApplyForm 接口报文
 */
export const buildLoanApplication = ({ values, product, phoneAuthCode }) => {
  const userType = String(product.customerType)
  const loanTerm = product.loanTermsUnitDesc === '年'
    ? String(Number(values.term) * 12)
    : String(values.term)
  const common = {
    productId: product.productId,
    productNm: product.productName,
    userType,
    loanMoney: String(values.amount),
    loanTerm,
    orgNos: values.orgNos || [],
    usageInf: values.usageInf,
    phoneAuthCode
  }

  if (userType === '1') {
    return {
      ...common,
      custName: values.enterpriseName,
      custCerNo: values.taxId,
      cpName: values.legalPersonName,
      cpCertNo: values.legalPersonId,
      cpCellPhone: values.mobile
    }
  }

  return {
    ...common,
    brName: values.borrowerName,
    brCertNo: values.borrowerId,
    brCellPhone: values.borrowerMobile
  }
}
